import { useDispatch, useSelector } from "react-redux";
import { TModalsState, setModalsData } from "../../api/store/commonSlice";
import { FC, useState } from "react";
import { useWeatherQuery } from "../../api/network/weatherApiServices";
import getWeatherIconPath from "../../assets/weather_icons";
import { useTranslation } from "react-i18next";
import { formattedDateTime } from "../../utils/dateTime";
import svgIcons from "../../assets/svg/svgIcons";
import { GOOGLEMAPS_ENDPOINTS } from "../../api/network/endpoints";

type TWeather = {
  temprature: number;
  wx: string;
  rain: number;
  wind: number;
  uv: number;
  humidity: number;
  icon: string;
  sunrise?: string;
  sunset?: string;
}

interface ModalWeatherForecastProps {
  dayOrNight: string;
  data: TWeather;
  temperatureUnit: 'F' | 'C';
}

const ModalWeatherForecast: FC<ModalWeatherForecastProps> = ({ dayOrNight = "day", data, temperatureUnit }) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "mapFleet.weatherForecast",
  });
  return (
    <div className={`p-1 space-y-4 ${dayOrNight === "day" ? "border-r-[1px]" : ""} border-gray-300`}>
      <p className="font-medium text-base leading-6 capitalize text-center">{dayOrNight}</p>
      <div className="space-y-2 text-accent-blue-dark justify-center text-center">
        <img src={data.icon} alt="weather-icon" className="mx-auto w-12 h-12" />
        <p className="font-semibold text-lg leading-5">{data.temprature}&deg;{temperatureUnit}</p>
        <p className="font-normal text-xs leading-tight">{data.wx}</p>
        {dayOrNight === "day" ? (
          <p className="font-normal text-xs leading-tight">
            {data?.sunrise && (
              <>
                {t('sunrise')}<br />
                {formattedDateTime(parseInt(data.sunrise) * 1000, 'h:mmaaa')}
              </>
            )}
          </p>
        ) : (
          <p className="font-normal text-xs leading-tight">
            {data?.sunset && (
              <>
                {t('sunset')}<br />
                {formattedDateTime(parseInt(data.sunset) * 1000, 'h:mmaaa')}
              </>
            )}
          </p>
        )}
      </div>
      <div className="space-y-2 font-normal text-[10px] leading-tight text-center">
        <p>{t("rain") + ":"} <span className="text-accent-blue-dark">{data.rain + "%"}</span></p>
        <p>{t("wind") + ":"} <span className="text-accent-blue-dark">{data.wind + (temperatureUnit === "F" ? "mi/h" : "km/h")}</span></p>
        <p>{t("uv") + ":"} <span className="text-accent-blue-dark">{data.uv + "mW/cm2"}</span></p>
        <p>{t("humidity") + ":"} <span className="text-accent-blue-dark">{data.humidity + "%"}</span></p>
      </div>
    </div>
  )
}

interface SpecificDayProps {
  dayOfWeek: string;
  day: TWeather;
  night: TWeather;
  temperatureUnit: 'F' | 'C';
}

const SpecificDay: FC<SpecificDayProps> = ({ dayOfWeek, day, night, temperatureUnit }) => {
  return (
    <div className="py-3 rounded-xl flex-col justify-between bg-[#C2DCF252] space-y-3">
      <p className="font-semibold text-lg leading-6 text-center">{dayOfWeek}</p>
      <div className="flex justify-between">
        <ModalWeatherForecast dayOrNight="day" data={day} temperatureUnit={temperatureUnit} />
        <ModalWeatherForecast dayOrNight="night" data={night} temperatureUnit={temperatureUnit} />
      </div>
    </div>
  )
}

type TSpecificDayData = {
  dayOfWeek: string;
  day: TWeather;
  night: TWeather;
}[]

interface WeatherForecastModalProps {
  latitude: number;
  longitude: number;
  address: string;
}

const WeatherForecastModal: FC<WeatherForecastModalProps> = ({ latitude, longitude, address }) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "mapFleet.weatherForecast",
  });
  const { t: tMain } = useTranslation("translation");

  const modalsState = useSelector((state: { commonReducer: { modals: TModalsState } }) => state.commonReducer.modals);
  const dispatch = useDispatch();

  const userIsImpersonated = useSelector(
      (state: { commonReducer: { userIsImpersonated: boolean } }) =>
        state.commonReducer.userIsImpersonated
    );

  const [temperatureUnit, setTemperatureUnit] = useState<"F" | "C">("F");

  const { data, isFetching, refetch: refetchWeatherData } = useWeatherQuery(
    {
      lat: latitude,
      lon: longitude,
      units: temperatureUnit === "C" ? 'm' : 'e'
    },
    { skip: !latitude || !longitude }
  );

  const dayPartData = data?.daypart[0];

  const SpecificDayData: TSpecificDayData = [
    {
      dayOfWeek: "Today",
      day: { //TODO : if value is null default value need to be change
        temprature: dayPartData?.temperature[0] ?? 0,
        wx: dayPartData?.wxPhraseShort[0] ?? "",
        rain: dayPartData?.precipChance[0] ?? 0,
        wind: dayPartData?.windSpeed[0] ?? 0,
        uv: dayPartData?.uvIndex[0] ?? 0,
        humidity: dayPartData?.relativeHumidity[0] ?? 0,
        icon: getWeatherIconPath(dayPartData?.iconCode[0]),
        sunrise: data?.sunriseTimeUtc[0]
      },
      night: {
        temprature: dayPartData?.temperature[1],
        wx: dayPartData?.wxPhraseShort[1],
        rain: dayPartData?.precipChance[1],
        wind: dayPartData?.windSpeed[1],
        uv: dayPartData?.uvIndex[1],
        humidity: dayPartData?.relativeHumidity[1],
        icon: getWeatherIconPath(dayPartData?.iconCode[1]),
        sunset: data?.sunsetTimeUtc[0]
      }
    },
    {
      dayOfWeek: "Tomorrow",
      day: {
        temprature: dayPartData?.temperature[2],
        wx: dayPartData?.wxPhraseShort[2],
        rain: dayPartData?.precipChance[2],
        wind: dayPartData?.windSpeed[2],
        uv: dayPartData?.uvIndex[2],
        humidity: dayPartData?.relativeHumidity[2],
        icon: getWeatherIconPath(dayPartData?.iconCode[2]),
        sunrise: data?.sunriseTimeUtc[1]
      },
      night: {
        temprature: dayPartData?.temperature[3],
        wx: dayPartData?.wxPhraseShort[3],
        rain: dayPartData?.precipChance[3],
        wind: dayPartData?.windSpeed[3],
        uv: dayPartData?.uvIndex[3],
        humidity: dayPartData?.relativeHumidity[3],
        icon: getWeatherIconPath(dayPartData?.iconCode[3]),
        sunset: data?.sunsetTimeUtc[1]
      }
    },
    {
      dayOfWeek: data?.dayOfWeek[2],
      day: {
        temprature: dayPartData?.temperature[4],
        wx: dayPartData?.wxPhraseShort[4],
        rain: dayPartData?.precipChance[4],
        wind: dayPartData?.windSpeed[4],
        uv: dayPartData?.uvIndex[4],
        humidity: dayPartData?.relativeHumidity[4],
        icon: getWeatherIconPath(dayPartData?.iconCode[4]),
        sunrise: data?.sunriseTimeUtc[2]
      },
      night: {
        temprature: dayPartData?.temperature[5],
        wx: dayPartData?.wxPhraseShort[5],
        rain: dayPartData?.precipChance[5],
        wind: dayPartData?.windSpeed[5],
        uv: dayPartData?.uvIndex[5],
        humidity: dayPartData?.relativeHumidity[5],
        icon: getWeatherIconPath(dayPartData?.iconCode[5]),
        sunset: data?.sunsetTimeUtc[2]
      }
    },
    {
      dayOfWeek: data?.dayOfWeek[3],
      day: {
        temprature: dayPartData?.temperature[6],
        wx: dayPartData?.wxPhraseShort[6],
        rain: dayPartData?.precipChance[6],
        wind: dayPartData?.windSpeed[6],
        uv: dayPartData?.uvIndex[6],
        humidity: dayPartData?.relativeHumidity[6],
        icon: getWeatherIconPath(dayPartData?.iconCode[6]),
        sunrise: data?.sunriseTimeUtc[3]
      },
      night: {
        temprature: dayPartData?.temperature[7],
        wx: dayPartData?.wxPhraseShort[7],
        rain: dayPartData?.precipChance[7],
        wind: dayPartData?.windSpeed[7],
        uv: dayPartData?.uvIndex[7],
        humidity: dayPartData?.relativeHumidity[7],
        icon: getWeatherIconPath(dayPartData?.iconCode[7]),
        sunset: data?.sunsetTimeUtc[3]
      }
    },
    {
      dayOfWeek: data?.dayOfWeek[4],
      day: {
        temprature: dayPartData?.temperature[8],
        wx: dayPartData?.wxPhraseShort[8],
        rain: dayPartData?.precipChance[8],
        wind: dayPartData?.windSpeed[8],
        uv: dayPartData?.uvIndex[8],
        humidity: dayPartData?.relativeHumidity[8],
        icon: getWeatherIconPath(dayPartData?.iconCode[8]),
        sunrise: data?.sunriseTimeUtc[4]
      },
      night: {
        temprature: dayPartData?.temperature[9],
        wx: dayPartData?.wxPhraseShort[9],
        rain: dayPartData?.precipChance[9],
        wind: dayPartData?.windSpeed[9],
        uv: dayPartData?.uvIndex[9],
        humidity: dayPartData?.relativeHumidity[9],
        icon: getWeatherIconPath(dayPartData?.iconCode[9]),
        sunset: data?.sunsetTimeUtc[4]
      }
    },
    {
      dayOfWeek: data?.dayOfWeek[5],
      day: {
        temprature: dayPartData?.temperature[10],
        wx: dayPartData?.wxPhraseShort[10],
        rain: dayPartData?.precipChance[10],
        wind: dayPartData?.windSpeed[10],
        uv: dayPartData?.uvIndex[10],
        humidity: dayPartData?.relativeHumidity[10],
        icon: getWeatherIconPath(dayPartData?.iconCode[10]),
        sunrise: data?.sunriseTimeUtc[5]
      },
      night: {
        temprature: dayPartData?.temperature[11],
        wx: dayPartData?.wxPhraseShort[11],
        rain: dayPartData?.precipChance[11],
        wind: dayPartData?.windSpeed[11],
        uv: dayPartData?.uvIndex[11],
        humidity: dayPartData?.relativeHumidity[11],
        icon: getWeatherIconPath(dayPartData?.iconCode[11]),
        sunset: data?.sunsetTimeUtc[5]
      }
    },
    {
      dayOfWeek: data?.dayOfWeek[6],
      day: {
        temprature: dayPartData?.temperature[12],
        wx: dayPartData?.wxPhraseShort[12],
        rain: dayPartData?.precipChance[12],
        wind: dayPartData?.windSpeed[12],
        uv: dayPartData?.uvIndex[12],
        humidity: dayPartData?.relativeHumidity[12],
        icon: getWeatherIconPath(dayPartData?.iconCode[12]),
        sunrise: data?.sunriseTimeUtc[6]
      },
      night: {
        temprature: dayPartData?.temperature[13],
        wx: dayPartData?.wxPhraseShort[13],
        rain: dayPartData?.precipChance[13],
        wind: dayPartData?.windSpeed[13],
        uv: dayPartData?.uvIndex[13],
        humidity: dayPartData?.relativeHumidity[13],
        icon: getWeatherIconPath(dayPartData?.iconCode[13]),
        sunset: data?.sunsetTimeUtc[6]
      }
    }
  ]

  const currentDayData = SpecificDayData[0].night; //TODO: Need to add condition for day and night

  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, showWeatherForecast: false }));
  };

  if (modalsState.showWeatherForecast === false) return null;

  return (
    <>
      <div className="justify-center items-start flex overflow-x-hidden overflow-y-auto fixed inset-0 z-overlay outline-none focus:outline-none">
        <div className="fixed w-full h-screen bg-modal-overlay z-overlay" onClick={hideModal}></div>
        <div className={`p-5 md:p-10 bg-gray-100 rounded-3xl shadow-2xl space-y-10 relative my-6 ${userIsImpersonated ? "mt-28" : "mt-[calc(72px)]"} mx-auto max-w-[calc(100vw-2rem)] w-[1320px] z-modal`}>
          <div className="flex justify-between">
            <div className="flex flex-col md:flex-row gap-4 md:gap-10 md:items-center">
              <h2 className="font-bold text-xl md:text-4xl">{t("heading")}</h2>
              {!isFetching && (<label
                htmlFor={'temperatureUnit'}
                className={`flex gap-2 justify-between items-center rounded-lg shadow-sm cursor-pointer w-fit`}>
                <span className="font-normal text-lg">{"C"}</span>
                <input
                  id={'temperatureUnit'}
                  type={"checkbox"}
                  checked={temperatureUnit === "F"}
                  onChange={(e) => setTemperatureUnit(e.target.checked ? "F" : "C")}
                  className="mt-1 w-12 h-11 px-3 disabled:bg-gray-200 disabled:border-gray-300 rounded-md text-sm shadow-sm sr-only peer focus-visible:outline-4 focus-visible:shadow-none"
                />
                <div
                  onClick={() =>
                    setTemperatureUnit((prev) => (prev === "F" ? "C" : "F"))
                  }
                  className="relative w-12 h-7 pointer-events-none touch-none bg-gray-200 rounded-full peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:start-1 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-blue-pale peer-focus:outline outline-2 outline-accent-blue-dark"
                ></div>
                <span className="font-normal text-lg">{"F"}</span>
              </label>)}
            </div>
            <img src={svgIcons.CloseIcon} alt="close-icon" onClick={hideModal} className="cursor-pointer" />
          </div>
          <div className="flex flex-col md:flex-row md:justify-between md:items-center space-y-6 md:space-y-0">
            <div className="space-y-2">
              <button type="button"
                className={`flex items-center gap-2 text-accent-blue-dark ${isFetching ? 'opacity-25 cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={() => refetchWeatherData?.()}>
                {tMain('admins.refresh_data')}
                <img src={svgIcons.RefreshIcon} alt="refresh-icon" className="w-5 h-5" />
              </button>
              <p className="font-bold text-xl md:text-3xl">{t("now")}</p>
              <div className="space-y-2">
                <p className="font-semibold text-lg break-words md:w-[497px]">{address}</p>
                <a className="inline-block font-normal text-base text-accent-blue-bright underline mt-2"
                  href={GOOGLEMAPS_ENDPOINTS.PLACE_URL(latitude, longitude)} target="_blank" rel="noreferrer"
                >{latitude + ", " + longitude}</a>
              </div>
            </div>
            <div className="flex gap-12">
              <div className="text-center space-y-2">
                <p className="font-bold text-4xl text-accent-blue-dark">{currentDayData?.temprature}&deg;{temperatureUnit}</p>
                <p className="font-semibold text-2xl text-accent-blue-dark">{currentDayData?.wx}</p>
                <div className="flex items-center justify-center font-normal text-lg space-x-2">
                  <p>{t("rain") + ":"} <span className="text-accent-blue-dark">{currentDayData?.rain + "%"}</span></p>
                  <p><span className="block size-2 bg-accent-blue-pale rounded-lg"></span></p>
                  <p>{t("wind") + ":"} <span className="text-accent-blue-dark">{`${currentDayData?.wind} ${temperatureUnit === "F" ? "mi/h" : "km/h"}`}</span></p>
                </div>
                <div className="flex items-center justify-center font-normal text-lg space-x-2">
                  <p>{t("uv") + ":"} <span className="text-accent-blue-dark">{currentDayData?.uv + "mW/cm2"}</span></p>
                  <p><span className="block size-2 bg-accent-blue-pale rounded-lg"></span></p>
                  <p>{t("humidity") + ":"} <span className="text-accent-blue-dark">{currentDayData?.humidity + "%"}</span></p>
                </div>
              </div>
              <div className="hidden md:flex items-center justify-center rounded-lg bg-[#C2DCF252] size-40">
                <img src={currentDayData?.icon} alt="weather-icon" className="w-24 h-24" />
              </div>
            </div>
          </div>
          <div className="flex justify-between space-x-2 md:overflow-visible overflow-x-auto pb-2">
            {SpecificDayData.map((day, index) => {
              return (
                <SpecificDay key={index} dayOfWeek={day.dayOfWeek} day={day.day} night={day.night} temperatureUnit={temperatureUnit} />
              )
            }
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default WeatherForecastModal;